import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { routeTree } from './routeTree.gen';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'sonner';
import { useAuthStore } from '@/features/auth';
import { getMemberInfo } from '@/entities/member';
import { initSentry } from './instrument/sentry';
import * as Sentry from '@sentry/react';
import { RouterErrorFallback } from '@/shared/ui/router-error-fallback';
import { SentryErrorFallback } from '@/shared/ui/sentry-error-fallback';
import { hasAuthToken } from '@/shared/lib/auth';
import { ThemeProvider } from './providers/ThemeProvider';

initSentry();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
    },
  },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const router = createRouter({
  routeTree,
  context: {
    queryClient,
    isLoggedIn: hasAuthToken(),
  },
  defaultErrorComponent: RouterErrorFallback,
});

// member 변경 시 라우터 context 갱신
useAuthStore.subscribe(() => {
  router.options.context = {
    ...router.options.context,
    isLoggedIn: hasAuthToken(),
  };
});

// Meta Pixel - 라우터 변경 시 PageView 트래킹
router.subscribe('onLoad', () => {
  if (typeof window.fbq !== 'undefined') {
    window.fbq('track', 'PageView');
  }
});

// 앱 초기화 시 토큰 유효성 검증 — 만료된 토큰으로 로그인 상태가 유지되는 것을 방지
// 토큰 없을 때 persist된 member 정리는 store의 onRehydrateStorage에서 처리
if (hasAuthToken()) {
  getMemberInfo()
    .then((member) => {
      useAuthStore.getState().setMember(member);
      router.invalidate();
    })
    .catch(() => {
      useAuthStore.getState().logout();
      router.invalidate();
    });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, resetError, eventId }) => (
        <SentryErrorFallback error={error} resetError={resetError} eventId={eventId} />
      )}
      showDialog={false}
    >
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <RouterProvider router={router} />
          <Toaster position='top-center' duration={2000} closeButton richColors />
          <ReactQueryDevtools initialIsOpen={true} />
        </ThemeProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
